/**
 * Texture loading utilities with validation.
 *
 * Provides helpers to catch common texture issues:
 * - Missing textures (not loaded in preload)
 * - Non-power-of-2 dimensions (breaks mipmapping)
 */

import type Phaser from "phaser";
import { getSpriteUrl } from "../assets";

function isPowerOf2(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

/**
 * Load a texture with URL resolution and register for validation.
 */
export function loadSprite(
  loader: Phaser.Loader.LoaderPlugin,
  key: string,
): void {
  const url = getSpriteUrl(key);
  if (!url) {
    console.error(`[Texture] Failed to resolve URL for sprite: "${key}"`);
    return;
  }
  loader.image(key, url);
}

/**
 * Validate that a texture exists and has power-of-2 dimensions.
 * Call this in create() after textures are loaded.
 */
export function validateTexture(
  textures: Phaser.Textures.TextureManager,
  key: string,
): void {
  if (!textures.exists(key)) {
    console.error(
      `[Texture] Missing texture: "${key}" - was it loaded in preload()?`,
    );
    return;
  }

  const texture = textures.get(key);
  const source = texture.getSourceImage();
  const width = source.width;
  const height = source.height;

  if (!isPowerOf2(width) || !isPowerOf2(height)) {
    console.error(
      `[Texture] Non-power-of-2 dimensions for "${key}": ${width}x${height}. ` +
        `Mipmapping will not work correctly. Use 256, 512, 1024, etc.`,
    );
  }
}

/**
 * Validate multiple textures at once.
 */
export function validateTextures(
  textures: Phaser.Textures.TextureManager,
  keys: string[],
): void {
  for (const key of keys) {
    validateTexture(textures, key);
  }
}
